import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "./utils";

/** Right-hand drawer (full screen under 768px). Esc closes; focus is trapped and restored. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  header,
  side = "right",
  className,
  onCloseAutoFocus,
}: {
  onCloseAutoFocus?: (e: Event) => void;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  header?: React.ReactNode;
  children: React.ReactNode;
  side?: "right" | "left";
  className?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)] data-[state=open]:animate-[fade-in_180ms_var(--ease-out-quart)]" />
        <Dialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            "fixed inset-y-0 z-50 flex w-full flex-col bg-raised shadow-float outline-none",
            side === "right"
              ? "right-0 border-l border-line md:w-[600px] data-[state=open]:animate-[sheet-in-right_220ms_var(--ease-out-expo)]"
              : "left-0 w-[280px] border-r border-line data-[state=open]:animate-[sheet-in-left_220ms_var(--ease-out-expo)]",
            className,
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-3.5">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-md font-medium tracking-title">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-sm text-ink-3">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">Details</Dialog.Description>
              )}
            </div>
            <div className="flex items-center gap-1">
              {header}
              <Dialog.Close
                aria-label="Close"
                className="inline-flex size-8 items-center justify-center rounded-sm text-ink-3 transition-colors hover:bg-hover hover:text-ink"
              >
                <X className="size-4" />
              </Dialog.Close>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
